import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Save, Filter, FileBox, MessageCircle, Send } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUSES = ['quote_requested', 'quoted', 'approved', 'in_production', 'shipped', 'completed', 'cancelled'];

const statusColor = (s) => ({
  quote_requested: 'bg-amber-50 text-amber-700 border-amber-200',
  quoted:          'bg-blue-50 text-blue-700 border-blue-200',
  approved:        'bg-emerald-50 text-emerald-700 border-emerald-200',
  in_production:   'bg-orange-50 text-orange-700 border-orange-200',
  shipped:         'bg-indigo-50 text-indigo-700 border-indigo-200',
  completed:       'bg-zinc-100 text-zinc-700 border-zinc-200',
  cancelled:       'bg-rose-50 text-rose-700 border-rose-200'
}[s] || 'bg-zinc-100 text-zinc-700 border-zinc-200');

const Admin = () => {
  const { t, lang } = useLang();
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(null); // order_number
  const [msgsByOrder, setMsgsByOrder] = useState({});
  const [drafts, setDrafts] = useState({});
  const [savingFor, setSavingFor] = useState(null);

  const loadOrders = async () => {
    const { data } = await axios.get(`${API}/admin/orders`);
    setOrders(data);
  };
  const loadUsers = async () => {
    const { data } = await axios.get(`${API}/admin/users`);
    setUsers(data);
  };

  useEffect(() => { loadOrders(); loadUsers(); }, []);

  const openOrder = async (order_number) => {
    if (expanded === order_number) { setExpanded(null); return; }
    setExpanded(order_number);
    if (!msgsByOrder[order_number]) {
      try {
        const { data } = await axios.get(`${API}/orders/${order_number}/messages`);
        setMsgsByOrder((p) => ({ ...p, [order_number]: data }));
      } catch (e) { console.error(e); }
    }
  };

  const updateOrder = async (order_number, partial) => {
    setSavingFor(order_number);
    try {
      const { data } = await axios.put(`${API}/admin/orders/${order_number}`, partial);
      setOrders((prev) => prev.map((o) => (o.order_number === order_number ? data : o)));
      toast({ title: 'OK', description: 'Güncellendi' });
    } catch (e) {
      console.error(e);
      toast({ title: 'Hata', description: 'Güncellenemedi' });
    } finally {
      setSavingFor(null);
    }
  };

  const sendReply = async (order_number) => {
    const txt = (drafts[order_number] || '').trim();
    if (!txt) return;
    try {
      await axios.post(`${API}/orders/${order_number}/messages`, { text: txt });
      const { data } = await axios.get(`${API}/orders/${order_number}/messages`);
      setMsgsByOrder((p) => ({ ...p, [order_number]: data }));
      setDrafts((p) => ({ ...p, [order_number]: '' }));
    } catch (e) {
      console.error(e);
      toast({ title: 'Hata', description: 'Gönderilemedi' });
    }
  };

  const filtered = filter === 'all' ? orders : orders.filter((o) => o.status === filter);

  return (
    <div className="bg-white min-h-screen">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <h1 className="text-3xl md:text-4xl font-bold text-zinc-900">{t.admin.title}</h1>
        <p className="text-zinc-600 mt-1 mb-6">{t.admin.subtitle}</p>

        <Tabs defaultValue="orders">
          <TabsList className="bg-zinc-100">
            <TabsTrigger value="orders">{t.admin.tabs.orders} <span className="ml-1.5 text-xs text-zinc-500">({orders.length})</span></TabsTrigger>
            <TabsTrigger value="users">{t.admin.tabs.users} <span className="ml-1.5 text-xs text-zinc-500">({users.length})</span></TabsTrigger>
          </TabsList>

          <TabsContent value="orders" className="mt-5">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-4 h-4 text-zinc-500" />
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-56 bg-white border-zinc-300"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">{t.admin.filterAll}</SelectItem>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{t.dashboard.statuses[s] || s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {filtered.length === 0 ? (
              <Card className="bg-white border-zinc-200 p-10 text-center text-zinc-500">{t.admin.noOrders}</Card>
            ) : (
              <div className="space-y-3">
                {filtered.map((o) => (
                  <Card key={o.id} className="bg-white border-zinc-200">
                    <button onClick={() => openOrder(o.order_number)} className="w-full p-5 text-left">
                      <div className="flex items-center gap-4 flex-wrap">
                        <div className="w-11 h-11 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-center justify-center shrink-0">
                          <FileBox className="w-5 h-5 text-orange-500" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-zinc-900 font-semibold">{o.order_number}</span>
                            <Badge className={`${statusColor(o.status)} border font-medium`}>
                              {t.dashboard.statuses[o.status] || o.status}
                            </Badge>
                          </div>
                          <div className="text-xs text-zinc-500 mt-1">
                            {new Date(o.created_at).toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-US')}
                            <span className="mx-1.5">·</span>
                            {(o.technology || '').toUpperCase()} · {o.material_label} · {t.dashboard.qty}: {o.quantity}
                          </div>
                          <div className="text-xs text-zinc-500 mt-1">
                            {t.admin.customer}: <span className="text-zinc-700">{o.contact_name}</span> · {o.contact_email}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          {o.quoted_price ? (
                            <div className="text-orange-500 font-bold">{o.quoted_price.toLocaleString('tr-TR')} ₺</div>
                          ) : (
                            <div className="text-zinc-400 text-xs italic">{t.dashboard.pending}</div>
                          )}
                        </div>
                      </div>
                    </button>

                    {expanded === o.order_number && (
                      <div className="border-t border-zinc-100 p-5 bg-zinc-50/50">
                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                          <div className="bg-white border border-zinc-200 rounded-lg p-4">
                            <div className="text-xs text-zinc-500 uppercase tracking-widest mb-2">{t.admin.setQuote}</div>
                            <QuoteEditor order={o} onSave={updateOrder} saving={savingFor === o.order_number} t={t} />
                          </div>
                          <div className="bg-white border border-zinc-200 rounded-lg p-4">
                            <div className="text-xs text-zinc-500 uppercase tracking-widest mb-2">{t.dashboard.file}</div>
                            <div className="text-sm text-zinc-700 truncate">{o.file_name}</div>
                            <div className="text-xs text-zinc-500 mt-1">{((o.file_size_bytes || 0) / 1024 / 1024).toFixed(2)} MB</div>
                            <Link to={`/dashboard/orders/${o.order_number}`}>
                              <Button variant="outline" className="mt-3 border-zinc-300 text-zinc-700 hover:text-orange-600 hover:border-orange-400">
                                {t.dashboard.view}
                              </Button>
                            </Link>
                          </div>
                        </div>

                        <div className="bg-white border border-zinc-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <MessageCircle className="w-4 h-4 text-orange-500" />
                            <span className="text-xs text-zinc-500 uppercase tracking-widest">{t.dashboard.messages}</span>
                          </div>
                          <div className="space-y-2 max-h-60 overflow-y-auto mb-3">
                            {(msgsByOrder[o.order_number] || []).map((m) => (
                              <div key={m.id} className={`flex ${m.author_role === 'admin' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm ${m.author_role === 'admin' ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-900'}`}>
                                  <div className="text-[10px] uppercase tracking-widest mb-0.5 opacity-70">{m.author_role === 'admin' ? t.dashboard.team : m.author_name}</div>
                                  <div className="whitespace-pre-wrap break-words">{m.text}</div>
                                </div>
                              </div>
                            ))}
                            {(!msgsByOrder[o.order_number] || msgsByOrder[o.order_number].length === 0) && (
                              <div className="text-zinc-400 text-xs text-center py-3">—</div>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Textarea
                              rows={2}
                              value={drafts[o.order_number] || ''}
                              onChange={(e) => setDrafts({ ...drafts, [o.order_number]: e.target.value })}
                              placeholder={t.admin.reply}
                              className="flex-1 bg-white border-zinc-300 focus-visible:ring-orange-500"
                            />
                            <Button onClick={() => sendReply(o.order_number)} className="bg-orange-500 hover:bg-orange-600 text-white border-0 self-end h-10">
                              <Send className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="users" className="mt-5">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {users.map((u) => (
                <Card key={u.user_id} className="bg-white border-zinc-200 p-4 flex items-center gap-3">
                  {u.picture ? (
                    <img src={u.picture} alt="" className="w-10 h-10 rounded-full border border-zinc-200" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold">{u.name?.[0]?.toUpperCase()}</div>
                  )}
                  <div className="min-w-0">
                    <div className="text-sm text-zinc-900 font-semibold truncate">{u.name} {u.is_admin && <Badge className="ml-1 bg-orange-500 text-white border-0">Admin</Badge>}</div>
                    <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                    {u.phone && <div className="text-xs text-zinc-400 truncate">{u.phone}</div>}
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const QuoteEditor = ({ order, onSave, saving, t }) => {
  const [price, setPrice] = useState(order.quoted_price || '');
  const [status, setStatus] = useState(order.status);

  return (
    <div className="space-y-2">
      <label className="text-xs text-zinc-600 block">{t.admin.quotedPrice}</label>
      <Input
        type="number"
        value={price}
        onChange={(e) => setPrice(e.target.value)}
        className="bg-white border-zinc-300 text-zinc-900 focus-visible:ring-orange-500"
        placeholder="0.00"
      />
      <label className="text-xs text-zinc-600 block mt-2">{t.admin.newStatus}</label>
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger className="bg-white border-zinc-300"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white">
          {STATUSES.map((s) => (
            <SelectItem key={s} value={s}>{t.dashboard.statuses[s] || s}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        onClick={() => onSave(order.order_number, { status, quoted_price: price ? parseFloat(price) : null })}
        disabled={saving}
        className="w-full mt-2 bg-orange-500 hover:bg-orange-600 text-white border-0"
      >
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
        {t.admin.update}
      </Button>
    </div>
  );
};

export default Admin;
